import { useState, type FormEvent } from 'react';
import type { AuthUser } from '@roll1/shared';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useI18n } from '../../i18n';

interface ProfileSettingsProps {
  user: AuthUser;
  error: string | null;
  onUpdateProfile: (displayName: string) => Promise<void>;
}

export default function ProfileSettings({ user, error, onUpdateProfile }: ProfileSettingsProps) {
  const { t } = useI18n();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    setBusy(true);
    try {
      await onUpdateProfile(displayName.trim());
    } finally {
      setBusy(false);
    }
  };

  const errorId = 'profile-settings-error';

  return (
    <div className="profile-settings" style={{ padding: '1rem' }}>
      <h3>{t('auth.profileTitle')}</h3>

      {error && <span id={errorId} role="alert" className="lobby-error">{error}</span>}

      <form onSubmit={handleSubmit}>
        <label>
          {t('auth.email')}
          <Input type="email" value={user.email} disabled readOnly />
        </label>

        <label>
          {t('auth.displayNameLabel')}
          <Input
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            disabled={busy}
            maxLength={50}
            required
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
          />
        </label>

        <div className="lobby-actions" style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
          <Button type="submit" disabled={busy || !displayName.trim()}>
            {busy ? t('auth.saveBusy') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
